import { PropsWithChildren, useCallback, useContext } from 'react';
import { ListBoxItemRenderProps, ListBoxSection, SelectStateContext } from 'react-aria-components';

import {
  Button,
  ButtonProps,
  Checkbox,
  Flex,
  Label,
  ListBox,
  ListBoxItem,
  PillSelectValue,
  Popover,
  Select,
  SelectProps,
  Separator,
  Text,
} from '@/components/core';
import { Id } from '@/utils';

import { AddIcon } from '../icons';
import { merge } from '../utils';

type AuxActionButtonProps = ButtonProps & {
  onPress?: () => void;
};

export const AuxActionButton = ({ onPress, children, ...rest }: AuxActionButtonProps) => {
  const state = useContext(SelectStateContext);

  const onPressFn = useCallback(() => {
    state?.close();
    onPress?.();
  }, [onPress, state]);

  return (
    <Button onPress={onPressFn} {...rest}>
      {children}
    </Button>
  );
};

export const SelectItem = ({ children }: PropsWithChildren) => (
  <Text size="xs" variant="default" className="p-xs rounded-md border-1 border-gray-200 bg-gray-50">
    {children}
  </Text>
);

interface SeelctPillsOptionProps<T> {
  value?: T | null;
}

export const SelectPillsOption = <T extends Id>({ value, children }: PropsWithChildren<SeelctPillsOptionProps<T>>) => (
  <ListBoxItem value={value} id={value?.id} className="group">
    {({ isSelected }: ListBoxItemRenderProps) => (
      <Flex className="gap-sm items-center">
        <Text className="grow">{children}</Text>
        <Checkbox isSelected={isSelected} />
      </Flex>
    )}
  </ListBoxItem>
);

interface SelectPillsProps<T> {
  optionRenderFn: (v?: T | null) => React.ReactNode;
  itemRenderFn?: (v?: T | null) => React.ReactNode;
  buttonComponent?: React.ReactNode;
  auxItems?: React.ReactNode;
  label?: string;
  isRequired?: boolean;
  items?: Iterable<T>;
}

export const SelectPills = <T extends Id>({
  optionRenderFn,
  items,
  label,
  itemRenderFn,
  buttonComponent,
  auxItems,
  isRequired,
  className,
  ...rest
}: SelectPillsProps<T> & Omit<SelectProps<T, 'multiple'>, 'selectionMode'>) => {
  return (
    <Select selectionMode="multiple" className={merge(className, 'group/select-pills')} {...rest}>
      <Flex col variant="plain" className="gap-xs">
        {label && <Label isRequired={isRequired}>{label}</Label>}
        {buttonComponent || (
          <Button>
            <div className="rounded-sm border-1 border-gray-200 p-[0.08rem] shadow-xs">
              <AddIcon className="size-[1.5]" />
            </div>
            <PillSelectValue>{(item?: T | null) => itemRenderFn?.(item)}</PillSelectValue>
          </Button>
        )}
      </Flex>
      <Popover>
        <ListBox className="max-h-[20rem] overflow-auto">
          <ListBoxSection aria-label="Options">{[...(items || [])]?.map(optionRenderFn)}</ListBoxSection>

          {auxItems && (
            <>
              <Separator orientation="horizontal" />
              {auxItems}
            </>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
};
